import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as sendGridApi from '../Api/sendGridSubuserApi';

const SENDGRID_SUBUSER_KEY = 'sendGridSubuser';

export const useSubuserStats = (companyId, options = {}) => {
	const { refetchInterval } = options;
	const { data, ...rest } = useQuery({
		queryKey: [SENDGRID_SUBUSER_KEY, 'stats', companyId],
		queryFn: () => sendGridApi.getSubusers(companyId),
		enabled: !!companyId,
		retry: false,
		refetchInterval,
	});

	return {
		data: data?.data || null,
		...rest,
	};
};

export const useEmailUsage = (companyId) => {
	const { data, ...rest } = useQuery({
		queryKey: [SENDGRID_SUBUSER_KEY, 'usage', companyId],
		queryFn: () => sendGridApi.getEmailUsage(companyId),
		enabled: !!companyId,
		retry: false,
	});

	return {
		data: data?.data || null,
		...rest,
	};
};

export const useCreateSubuser = (companyId) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data = {}) => sendGridApi.createSubuser(companyId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [SENDGRID_SUBUSER_KEY, 'stats', companyId],
			});
			queryClient.invalidateQueries({
				queryKey: [SENDGRID_SUBUSER_KEY, 'usage', companyId],
			});
		},
	});
};

export const useUpdateSubuserLimits = (companyId) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data = {}) => sendGridApi.updateSubuserLimits(companyId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [SENDGRID_SUBUSER_KEY, 'stats', companyId],
			});
			queryClient.invalidateQueries({
				queryKey: [SENDGRID_SUBUSER_KEY, 'usage', companyId],
			});
		},
	});
};

export const useToggleSubuserStatus = (companyId) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data) => sendGridApi.toggleSubuserStatus(companyId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [SENDGRID_SUBUSER_KEY, 'stats', companyId],
			});
			queryClient.invalidateQueries({
				queryKey: [SENDGRID_SUBUSER_KEY, 'usage', companyId],
			});
		},
	});
};

export const useRetryActivation = (companyId) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => sendGridApi.retryActivation(companyId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [SENDGRID_SUBUSER_KEY, 'stats', companyId],
			});
			queryClient.invalidateQueries({
				queryKey: [SENDGRID_SUBUSER_KEY, 'usage', companyId],
			});
		},
	});
};

export const useCheckEmailLimit = (companyId) => {
	const { data, ...rest } = useQuery({
		queryKey: [SENDGRID_SUBUSER_KEY, 'limit', companyId],
		queryFn: () => sendGridApi.checkEmailLimit(companyId),
		enabled: !!companyId,
		retry: false,
	});

	return {
		data: data?.data || null,
		...rest,
	};
};
